/**
 * Schema.org JSON-LD generators for MusicMagic.io
 * Provides type-safe functions for generating structured data with localization support
 */

import config, { BASE_URL } from '@/lib/config';
import { getTranslations } from 'next-intl/server';
import type { Playlist } from '@/lib/data/types';
import type { PlaylistContentData } from '@/lib/data/loaders/mongodb/playlists';
import type { Locale } from '@/i18n/routing';

// Base schema types
export interface SchemaObject {
  '@type': string;
  [key: string]: any;
}

// Specific schema interfaces
export interface OrganizationSchema extends SchemaObject {
  '@type': 'Organization';
  '@id': string;
  name: string;
  url: string;
  email: string;
  logo: {
    '@id': string;
    '@type': 'ImageObject';
    url: string;
    width: number;
    height: number;
  };
  sameAs: string[];
  founder?: {
    '@type': 'Person';
    name: string;
    jobTitle: string;
  };
  foundingDate?: string;
}

export interface WebSiteSchema extends SchemaObject {
  '@type': 'WebSite';
  '@id': string;
  name: string;
  url: string;
  inLanguage: string;
  publisher: {
    '@id': string;
  };
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export interface BreadcrumbSchema extends SchemaObject {
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }>;
}

export interface CollectionPageSchema extends SchemaObject {
  '@type': 'CollectionPage';
  '@id': string;
  name: string;
  description: string;
  url: string;
  inLanguage: string;
  isPartOf: {
    '@id': string;
  };
  about?: {
    '@id': string;
  };
}

export interface MusicPlaylistSchema extends SchemaObject {
  '@type': 'MusicPlaylist';
  '@id': string;
  name: string;
  description: string;
  url: string;
  numTracks: number;
  track?: Array<{
    '@type': 'MusicRecording';
    position: number;
    name: string;
    byArtist: {
      '@type': 'MusicGroup';
      name: string;
    } | Array<{
      '@type': 'MusicGroup';
      name: string;
    }>;
  }>;
  image?: string;
  thumbnailUrl?: string;
  inLanguage: string;
  datePublished?: string;
  dateModified?: string;
  genre?: string[];
  keywords?: string;
  sameAs?: string[];
  creator?: {
    '@type': 'Person';
    name: string;
    url?: string;
  };
}

export interface FAQPageSchema extends SchemaObject {
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

export interface WebPageSchema extends SchemaObject {
  '@type': 'WebPage';
  '@id': string;
  url: string;
  name?: string;
  description: string;
  isPartOf: {
    '@id': string;
  };
  about?: {
    '@id': string;
  };
  inLanguage?: string;
}

export interface ArticleSchema extends SchemaObject {
  '@type': 'Article';
  '@id': string;
  headline: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified: string;
  author: {
    '@type': 'Person';
    name: string;
    url?: string;
  };
  publisher: {
    '@id': string;
  };
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
}

/**
 * Generate Organization Schema
 */
export function generateOrganizationSchema(): OrganizationSchema {
  const orgConfig = config.organization;
  
  return {
    '@type': 'Organization',
    '@id': orgConfig.schemaIds.organization,
    name: orgConfig.name,
    url: orgConfig.url,
    email: `mailto:${orgConfig.email}`,
    logo: {
      '@id': orgConfig.schemaIds.logo,
      '@type': 'ImageObject',
      url: orgConfig.logo.url,
      width: orgConfig.logo.width,
      height: orgConfig.logo.height
    },
    sameAs: orgConfig.sameAs,
    founder: {
      '@type': 'Person',
      name: orgConfig.founder.name,
      jobTitle: orgConfig.founder.jobTitle
    },
    foundingDate: orgConfig.foundingDate
  } as OrganizationSchema;
}

/**
 * Generate WebSite Schema with search action
 */
export function generateWebSiteSchema(locale: string = 'en'): WebSiteSchema {
  const orgConfig = config.organization;
  
  return {
    '@type': 'WebSite',
    '@id': orgConfig.schemaIds.website,
    name: config.site.name,
    url: orgConfig.url,
    inLanguage: locale,
    publisher: {
      '@id': orgConfig.schemaIds.organization
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/playlists?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  } as WebSiteSchema;
}

/**
 * Generate Breadcrumb Schema with localized names
 */
export async function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
  locale: Locale = 'en'
): Promise<BreadcrumbSchema> {
  // Get translations for breadcrumb items
  const t = await getTranslations({ locale });
  
  // Always start with Home
  const breadcrumbItems = [
    {
      '@type': 'ListItem' as const,
      position: 1,
      name: t('common.global.header.navigation.home'),
      item: BASE_URL + '/'
    }
  ];
  
  // Add additional items
  items.forEach((item, index) => {
    breadcrumbItems.push({
      '@type': 'ListItem' as const,
      position: index + 2,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    });
  });
  
  return {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems
  } as BreadcrumbSchema;
}

/**
 * Generate Collection Page Schema for playlist listings
 */
export async function generateCollectionPageSchema(
  title: string,
  description: string,
  url: string,
  locale: Locale = 'en'
): Promise<CollectionPageSchema> {
  const orgConfig = config.organization;
  
  return {
    '@type': 'CollectionPage',
    '@id': `${url}#collection`,
    name: title,
    description: description,
    url: url,
    inLanguage: locale,
    isPartOf: {
      '@id': orgConfig.schemaIds.website
    },
    about: {
      '@id': orgConfig.schemaIds.organization
    }
  } as CollectionPageSchema;
}

/**
 * Generate Music Playlist Schema from playlist content data (MongoDB)
 */
export function generateMusicPlaylistSchemaFromContent(
  playlist: PlaylistContentData,
  locale: Locale = 'en'
): MusicPlaylistSchema {
  const playlistUrl = `${BASE_URL}/playlists/${playlist.slug}`;
  
  const schema: MusicPlaylistSchema = {
    '@type': 'MusicPlaylist',
    '@id': `${playlistUrl}#playlist`,
    name: playlist.name,
    description: playlist.description || playlist.seo_meta?.description || '',
    url: playlistUrl,
    numTracks: playlist.totalTracks || 0,
    inLanguage: locale,
    datePublished: playlist.createdAt ? new Date(playlist.createdAt).toISOString() : (playlist.created_at ? new Date(playlist.created_at).toISOString() : new Date().toISOString()),
    dateModified: playlist.spotifyLastUpdatedAt ? new Date(playlist.spotifyLastUpdatedAt).toISOString() : (playlist.updated_at ? new Date(playlist.updated_at).toISOString() : new Date().toISOString())
  } as MusicPlaylistSchema;
  
  // Add tracks if available
  if (playlist.tracks && playlist.tracks.length > 0) {
    schema.track = playlist.tracks.map((track, index) => ({
      '@type': 'MusicRecording' as const,
      position: index + 1,
      name: track.name,
      byArtist: track.artists && track.artists.includes(',') 
        ? track.artists.split(',').map(artist => ({
            '@type': 'MusicGroup' as const,
            name: artist.trim()
          }))
        : {
            '@type': 'MusicGroup' as const,
            name: track.artists || 'Unknown Artist'
          }
    }));
  }
  
  // Add image
  if (playlist.image) {
    schema.image = playlist.image;
    schema.thumbnailUrl = playlist.image;
  }
  
  // Add genres
  if (playlist.genres && playlist.genres.length > 0) {
    schema.genre = playlist.genres.map(g => g.name);
  }
  
  // Add keywords from tags
  if (playlist.tags && playlist.tags.length > 0) {
    schema.keywords = playlist.tags.map(t => t.name).join(', ');
  }
  
  // Add Spotify URL
  if (playlist.spotifyPlaylistId) {
    schema.sameAs = [`https://open.spotify.com/playlist/${playlist.spotifyPlaylistId}`];
  }
  
  // Add creator if available
  if (playlist.createdBy) {
    schema.creator = {
      '@type': 'Person',
      name: playlist.createdBy.name,
      ...(playlist.createdBy.spotify_id && {
        url: `https://open.spotify.com/user/${playlist.createdBy.spotify_id}`
      })
    };
  }
  
  return schema;
}

/**
 * Generate Music Playlist Schema from playlist type (PostgreSQL)
 */
export function generateMusicPlaylistSchema(
  playlist: Playlist,
  locale: Locale = 'en'
): MusicPlaylistSchema {
  const playlistUrl = `${BASE_URL}/playlists/${playlist.slug}`;
  
  const schema: MusicPlaylistSchema = {
    '@type': 'MusicPlaylist',
    '@id': `${playlistUrl}#playlist`,
    name: playlist.name,
    description: playlist.description || playlist.content?.seo_meta?.description || '',
    url: playlistUrl,
    numTracks: playlist.totalTracks || 0,
    inLanguage: locale,
    datePublished: playlist.createdAt ? new Date(playlist.createdAt).toISOString() : new Date().toISOString(),
    dateModified: playlist.spotifyLastUpdatedAt ? new Date(playlist.spotifyLastUpdatedAt).toISOString() : new Date().toISOString()
  } as MusicPlaylistSchema;
  
  // Add tracks if available
  if (playlist.tracks && playlist.tracks.length > 0) {
    schema.track = playlist.tracks.map((track: any, index: number) => ({
      '@type': 'MusicRecording' as const,
      position: index + 1,
      name: track.name,
      byArtist: track.artists && track.artists.includes(',')
        ? track.artists.split(',').map((artist: string) => ({
            '@type': 'MusicGroup' as const,
            name: artist.trim()
          }))
        : {
            '@type': 'MusicGroup' as const,
            name: track.artists || 'Unknown Artist'
          }
    }));
  }
  
  // Add image
  if (playlist.image) {
    schema.image = playlist.image;
    schema.thumbnailUrl = playlist.image;
  }
  
  // Add genres
  if (playlist.genres && playlist.genres.length > 0) {
    schema.genre = playlist.genres.map((g: any) => g.name);
  }
  
  // Add keywords from tags
  if (playlist.tags && playlist.tags.length > 0) {
    schema.keywords = playlist.tags.map((t: any) => t.name).join(', ');
  }
  
  // Add Spotify URL
  if (playlist.spotifyPlaylistId) {
    schema.sameAs = [`https://open.spotify.com/playlist/${playlist.spotifyPlaylistId}`];
  }
  
  // Add creator if available
  if (playlist.createdBy) {
    schema.creator = {
      '@type': 'Person',
      name: playlist.createdBy.name,
      ...(playlist.createdBy.spotify_id && {
        url: `https://open.spotify.com/user/${playlist.createdBy.spotify_id}`
      })
    };
  }
  
  return schema;
}

/**
 * Generate FAQ Page Schema
 */
export function generateFAQPageSchema(
  faqs: Array<{ question: string; answer: string }>
): FAQPageSchema {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question' as const,
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: faq.answer
      }
    }))
  } as FAQPageSchema;
}

/**
 * Generate WebPage Schema
 */
export function generateWebPageSchema(options: {
  url: string;
  name?: string;
  description: string;
  locale?: string;
}): WebPageSchema {
  const { url, name, description, locale = 'en' } = options;
  const orgConfig = config.organization;
  
  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    ...(name && { name }),
    description,
    isPartOf: {
      '@id': orgConfig.schemaIds.website
    },
    about: {
      '@id': orgConfig.schemaIds.organization
    },
    inLanguage: locale
  } as WebPageSchema;
}

/**
 * Generate Article Schema for blog posts
 */
export function generateArticleSchema(options: {
  title: string;
  description: string;
  url: string;
  image?: string;
  publishedDate: string;
  modifiedDate: string;
  author: {
    name: string;
    url?: string;
  };
}): ArticleSchema {
  const { title, description, url, image, publishedDate, modifiedDate, author } = options;
  const orgConfig = config.organization;
  
  return {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: title,
    description,
    ...(image && { image }),
    datePublished: publishedDate,
    dateModified: modifiedDate,
    author: {
      '@type': 'Person',
      name: author.name,
      ...(author.url && { url: author.url })
    },
    publisher: {
      '@id': orgConfig.schemaIds.organization
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${url}#webpage`
    }
  } as ArticleSchema;
}

/**
 * Generate a complete graph of schemas for a page
 */
export function generateSchemaGraph(schemas: SchemaObject[]): { '@context': string; '@graph': SchemaObject[] } {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas
  };
} 